/* eslint-disable no-console -- want to have some errors written into console */
import type * as ReplaceSubscription from '@ori-events/replace-subscription';
import * as ReplaceSubscriptionFailed from '@ori-events/replace-subscription-failed';
import type { PositionData } from '@ori/analytics-data';

import type { ReplaceSubscriptionResponse } from '../guards/replaceSubscriptionResponse';
import { isReplaceSubscriptionErrorResponse } from '../guards/replaceSubscriptionResponse';
import { convertToCamelCase, getHttpRequestSettings, log } from '../helpers';
import type { AppConfiguration } from '../models';
import { HttpMethod, RequestMethod, RequestUrl } from '../models';
import { getUrl, getUrlOptions } from '~/utils';
import type { GetUrlOptions } from '~/utils';

interface Data extends ReplaceSubscription.Data, PositionData {}

interface ReplacementData {
  DroppingId: number;
  ItemCode: string;
}

const fetchData = async (
  options: GetUrlOptions,
  data: ReplacementData,
): Promise<ReplaceSubscriptionResponse> =>
  getUrl<ReplaceSubscriptionResponse>({
    ...options,
    method: HttpMethod.Put,
    isAuthorizationRequired: true,
    data,
  });

export default async (
  { detail: { droppingId, itemCode, subscriptionId } }: CustomEvent<Data>,
  configuration: AppConfiguration,
) => {
  // STEP 1 - Update through API
  const { customerId, globalApiUrl } = configuration;
  const { urlOptions } = getUrlOptions({
    configuration,
    basePath: `/customers/${customerId}/subscriptions/${subscriptionId}/subscriptionItems/0`,
    host: globalApiUrl,
  });

  const fetcher = async () => {
    const promise = fetchData(urlOptions, { DroppingId: droppingId, ItemCode: itemCode });
    promise
      .then((result) => {
        log(`Subscription successfully replaced: ${result}`);
      })
      .catch((error: unknown) => {
        const message = String(error);
        console.error(
          `Error while replacing subscription in replaceSubscription event: ${message}`,
        );
      });

    return promise;
  };

  try {
    await fetcher();
  } catch (error: unknown) {
    const message = String(error);
    console.error(`Error while replacing subscription in replaceSubscription event: ${message}`);

    return false;
  }

  // STEP 2 - Update shopping context online endpoints
  const request = await fetch(
    RequestUrl.GetShoppingContext,
    getHttpRequestSettings(undefined, RequestMethod.Post),
  );

  const response = convertToCamelCase<ReplaceSubscriptionResponse>(
    (await request.json()) as object,
  );

  if (isReplaceSubscriptionErrorResponse(response)) {
    const errorEvent = new CustomEvent<ReplaceSubscriptionFailed.Data>(
      ReplaceSubscriptionFailed.Name,
      {
        detail: response,
      },
    );
    window.dispatchEvent(errorEvent);

    return false;
  }

  return response;
};
